import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ProductForm from "../components/ProductForm";
import { useSettings } from "../contexts/SettingsContext";
import { db, type Product, parseBundle, type Bundle } from "../database/db";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import BundleForm from "../components/BundleForm";
import { formatCurrency } from "../utils/formatters";

import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import DiscountIcon from "@mui/icons-material/Discount";

export default function InventoryPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
  const { settings } = useSettings();
  const [isBundleFormOpen, setIsBundleFormOpen] = useState(false);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | undefined>();

  useEffect(() => {
    loadProducts();
    loadBundles();
  }, []);

  const loadProducts = async () => {
    const allProducts = await db.products.toArray();
    setProducts(allProducts);
  };

  const loadBundles = async () => {
    const allBundles = await db.bundles.toArray();
    setBundles(allBundles);
  };

  const handleAddProduct = async (product: Omit<Product, "id">) => {
    await db.products.add(product as Product);
    loadProducts();
  };

  const handleEditProduct = async (product: Omit<Product, "id">) => {
    if (selectedProduct) {
      await db.products.update(selectedProduct.id, product);
      setSelectedProduct(undefined);
      loadProducts();
    }
  };

  const handleDeleteProduct = async (id: number) => {
    if (confirm("Are you sure you want to delete this product?")) {
      await db.products.delete(id);
      loadProducts();
    }
  };

  const handleAddBundle = async (bundle: Omit<Bundle, "id">) => {
    await db.bundles.add(bundle as Bundle);
    loadBundles();
  };

  const handleEditBundle = async (bundle: Omit<Bundle, "id">) => {
    if (selectedBundle) {
      await db.bundles.update(selectedBundle.id, bundle);
      setSelectedBundle(undefined);
      loadBundles();
    }
  };

  const handleDeleteBundle = async (id: number) => {
    if (confirm("Are you sure you want to delete this bundle?")) {
      await db.bundles.delete(id);
      loadBundles();
    }
  };

  const openEditForm = (product: Product) => {
    setSelectedProduct(product);
    setIsFormOpen(true);
  };

  const sortedProducts = [...products].sort((a, b) => {
    // First sort by type (products first, then coupons)
    if (a.price >= 0 !== b.price >= 0) {
      return b.price >= 0 ? 1 : -1;
    }
    // Then sort by name
    return a.name.localeCompare(b.name);
  });

  const { products: allProducts, discounts } = useMemo(() => {
    return {
      products: sortedProducts.filter((p) => p.price >= 0),
      discounts: sortedProducts.filter((p) => p.price < 0),
    };
  }, [sortedProducts]);

  const renderTableRow = (product: Product) => {
    const isDiscount = product.price < 0;
    return (
      <TableRow
        key={product.id}
        sx={{
          backgroundColor: isDiscount ? "rgba(156, 39, 176, 0.04)" : "inherit",
        }}>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isDiscount ? (
              <LocalOfferIcon color="secondary" />
            ) : (
              <ShoppingBasketIcon color="primary" />
            )}

            <Box
              sx={{
                width: 40,
                height: 40,
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
                overflow: "hidden",
              }}>
              {product.imageBlob ? (
                <img
                  src={URL.createObjectURL(product.imageBlob)}
                  alt={product.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <ImageNotSupportedIcon color="disabled" />
              )}
            </Box>
            <Typography>{product.name}</Typography>
          </Box>
        </TableCell>
        <TableCell>{product.sku}</TableCell>
        <TableCell align="right">
          {`${settings.currencySymbol}${Math.abs(product.price).toFixed(
            settings.currencyRounding
          )}`}
          {isDiscount && " (Discount)"}
        </TableCell>
        <TableCell align="right">{product.originalStockQuantity}</TableCell>
        <TableCell align="center">
          <IconButton
            onClick={() => navigate(`/reports?productId=${product.id}`)}>
            <VisibilityIcon />
          </IconButton>
          <IconButton onClick={() => openEditForm(product)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDeleteProduct(product.id)}>
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  const renderTable = (
    items: Product[],
    title: string,
    icon: React.ReactNode
  ) => (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        {icon} {title}
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Stock</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{items.map(renderTableRow)}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderBundleTable = () => {
    const calculateBundleTotal = (bundle: Bundle) => {
      const requirements = parseBundle(bundle).requirements;
      return requirements.reduce((total, req) => {
        const product = products.find((p) => p.id === req.requiredProductId);
        return total + (product ? product.price * req.count : 0);
      }, 0);
    };

    const getItemCount = (bundle: Bundle) => {
      const requirements = parseBundle(bundle).requirements;
      return requirements.reduce((total, req) => total + req.count, 0);
    };

    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <DiscountIcon /> Bundles
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Items</TableCell>
                <TableCell align="right">Original Price</TableCell>
                <TableCell align="right">Bundle Price</TableCell>
                <TableCell align="right">Savings</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bundles.map((bundle) => {
                return (
                  <TableRow key={bundle.id}>
                    <TableCell>{bundle.name}</TableCell>
                    <TableCell align="right">
                      {getItemCount(bundle)} items
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        textDecoration: "line-through",
                        color: "text.secondary",
                      }}>
                      {formatCurrency(calculateBundleTotal(bundle), settings)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(
                        calculateBundleTotal(bundle) - bundle.discountAmount,
                        settings
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "success.main" }}>
                      {formatCurrency(bundle.discountAmount, settings)} (
                      {(
                        (bundle.discountAmount / calculateBundleTotal(bundle)) *
                        100
                      ).toFixed(1)}
                      %)
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => {
                          setSelectedBundle(bundle);
                          setIsBundleFormOpen(true);
                        }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteBundle(bundle.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Add cleanup for object URLs
  useEffect(() => {
    return () => {
      products.forEach((product) => {
        if (product.imageBlob) {
          URL.revokeObjectURL(URL.createObjectURL(product.imageBlob));
        }
      });
    };
  }, [products]);

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          paddingX: 4,
          paddingY: 6,
        }}>
        {renderTable(allProducts, "Products", <ShoppingBasketIcon />)}
        {renderTable(discounts, "Discounts", <LocalOfferIcon />)}
        {renderBundleTable()}

        <SpeedDial
          ariaLabel="Add menu"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            alignItems: "center",
          }}
          icon={<AddIcon sx={{ fontSize: "32px" }} />}>
          <SpeedDialAction
            sx={{
              width: "50px",
              height: "50px",
              margin: 1,
              "& .MuiSpeedDialAction-staticTooltipLabel": {
                whiteSpace: "nowrap",
              },
            }}
            icon={
              <>
                <ShoppingBasketIcon sx={{ fontSize: "20px" }} />{" "}
                <LocalOfferIcon sx={{ fontSize: "20px" }} />
              </>
            }
            tooltipTitle="Add Product / Discount"
            tooltipOpen={true}
            onClick={() => setIsFormOpen(true)}
          />
          <SpeedDialAction
            sx={{
              width: "50px",
              height: "50px",
              margin: 1,
              "& .MuiSpeedDialAction-staticTooltipLabel": {
                whiteSpace: "nowrap",
              },
            }}
            icon={<DiscountIcon sx={{ fontSize: "20px" }} />}
            tooltipTitle="Add Bundle"
            tooltipOpen={true}
            onClick={() => setIsBundleFormOpen(true)}
          />
        </SpeedDial>

        <ProductForm
          open={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setSelectedProduct(undefined);
          }}
          onSave={selectedProduct ? handleEditProduct : handleAddProduct}
          initialProduct={selectedProduct}
        />

        <BundleForm
          open={isBundleFormOpen}
          onClose={() => {
            setIsBundleFormOpen(false);
            setSelectedBundle(undefined);
          }}
          onSave={selectedBundle ? handleEditBundle : handleAddBundle}
          products={allProducts}
          initialBundle={selectedBundle}
        />
      </Box>
    </Container>
  );
}
